import React from 'react';
import PropTypes from 'prop-types';
import '../styles/ChatPopup.css';

/**
 * ChatPopup component for user interaction with the AI assistant
 * 
 * @param {Object} props - Component properties
 * @param {boolean} props.open - Whether the chat popup is open
 * @param {Array} props.messages - Array of message objects with sender and text properties
 * @param {string} props.userInput - Current user input text
 * @param {Function} props.onInputChange - Handler for input changes
 * @param {Function} props.onSend - Handler for sending messages
 * @param {Function} props.onClose - Handler for closing the chat popup
 * @param {Object} props.chatBodyRef - Ref for the chat body element
 * @param {Function} props.formatFinancialData - Function to format financial data in messages
 * @returns {JSX.Element|null} ChatPopup component or null if not open
 */
const ChatPopup = ({
  open,
  messages,
  userInput,
  onInputChange,
  onSend,
  onClose,
  chatBodyRef,
  formatFinancialData
}) => {
  if (!open) return null;

  return (
    <div className="chat-popup">
      <div className="chat-header">
        <div className="logo"><h3>CP Finance Dashboard</h3></div>
        <div className="chat-header-controls">
          <button className="toggle-button" onClick={onClose} aria-label="Toggle chat">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
        </div>
      </div>
      <div className="chat-body" ref={chatBodyRef}>
        {messages.map((message, idx) => (
          <div key={idx} className={`chat-message ${message.sender}`}>
            {formatFinancialData(message.text)}
          </div>
        ))}
      </div>
      <div className="chat-footer">
        <input
          type="text"
          value={userInput}
          onChange={onInputChange}
          placeholder="Type your question..."
          onKeyPress={e => e.key === 'Enter' && onSend()}
        />
        <button className="send-button" onClick={onSend} aria-label="Send message">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
        </button>
      </div>
    </div>
  );
};

ChatPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      sender: PropTypes.oneOf(['user', 'bot']).isRequired,
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired
    })
  ).isRequired,
  userInput: PropTypes.string.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  chatBodyRef: PropTypes.object.isRequired,
  formatFinancialData: PropTypes.func.isRequired
};

export default React.memo(ChatPopup);
